import React, { useState } from 'react';
import Button from '../Ui/Button';
import Input from '../Ui/Input';
import Card from '../Ui/Card';
import { RecruiterFormData } from '../types';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

interface RecruiterRegistrationProps {
    onRegister: (data: RecruiterFormData) => void;
    theme: 'light' | 'dark';
}

const RecruiterRegistration = ({ onRegister, theme }: RecruiterRegistrationProps) => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState<RecruiterFormData>({
        company: '',
        recruiter: '',
        email: '',
        role: '',
        skills: '',
        location: ''
    });

    const isFormValid =
        formData.company &&
        formData.recruiter &&
        formData.email &&
        formData.role &&
        formData.skills &&
        formData.location;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isFormValid) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            const response = await fetch('https://wehireindia.com/wehire_api/recruiters/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error('Failed to submit recruiter request');
            }

            toast.success('Access request submitted successfully 🚀');

            // Reset form
            setFormData({
                company: '',
                recruiter: '',
                email: '',
                role: '',
                skills: '',
                location: '',
            });

            // Navigate back to home after successful submission
            navigate('/');

        } catch (error) {
            toast.error('Something went wrong. Please try again ❌');
            console.error(error);
        }
    };

    return (
        <div className="max-w-lg mx-auto py-12 px-6">
            <Button variant="text" onClick={() => navigate('/')} theme={theme} className="mb-4 pl-0">
                ← Back to Home
            </Button>
            <Card title="Recruiter Access" theme={theme}>
                <form onSubmit={handleSubmit}>
                    <Input
                        label="Company Name"
                        name="company"
                        value={formData.company}
                        onChange={e => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Enter company name"
                        required
                        theme={theme}
                    />
                    <Input
                        label="Recruiter Name"
                        name="recruiter"
                        value={formData.recruiter}
                        onChange={e => setFormData({ ...formData, recruiter: e.target.value })}
                        placeholder="Enter your name"
                        required
                        theme={theme}
                    />
                    <Input
                        label="Work Email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter work email"
                        required
                        theme={theme}
                    />

                    <div className={`my-6 border-t pt-6 ${theme === 'dark' ? 'border-zinc-800' : 'border-zinc-100'}`}>
                        <h4 className={`text-xs font-black uppercase tracking-widest mb-4 ${theme === 'dark' ? 'text-violet-400' : 'text-violet-600'}`}>Hiring Needs</h4>
                        <Input
                            label="Role Required"
                            placeholder="e.g. Senior Java Dev"
                            name="role"
                            value={formData.role}
                            onChange={e => setFormData({ ...formData, role: e.target.value })}
                            required
                            theme={theme}
                        />
                        <Input
                            label="Key Skills"
                            placeholder="e.g. React, Node, AWS"
                            name="skills"
                            value={formData.skills}
                            onChange={e => setFormData({ ...formData, skills: e.target.value })}
                            required
                            theme={theme}
                        />
                        <Input
                            label="Job Location"
                            name="location"
                            value={formData.location}
                            onChange={e => setFormData({ ...formData, location: e.target.value })}
                            placeholder="City"
                            required
                            theme={theme}
                        />
                    </div>

                    <Button
                        type="submit"
                        fullWidth
                        theme={theme}
                        disabled={!isFormValid}
                    >
                        Request Access
                    </Button>
                </form>
            </Card>
        </div>
    );
};

export default RecruiterRegistration;